'use client';
import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ChevronRight, Upload, FileText, RefreshCw, ClipboardList, User, Phone, MapPin, MessageCircle, CheckCircle2, AlertCircle, LogIn, Eye, Lock,  } from 'lucide-react';
import Link from 'next/link';
import TopNav from '@/components/layout/TopNav';
import { useAuth } from '@/contexts/AuthContext';
import { centres } from '@/data/locations';
import { hospitalConfig } from '@/data/hospitalConfig';
import {
  generatePharmacyRequestId,
  buildMedicineRequestWhatsAppMessage,
  buildRefillRequestWhatsAppMessage,
  openPharmacyWhatsApp,
  DEMO_PHARMACY_PRESCRIPTIONS,
  type DemoPharmacyPrescription,
} from '@/data/pharmacy';
import Icon from '@/components/ui/AppIcon';


// ─── Types ────────────────────────────────────────────────────────────────────

type PharmacyView =
  | 'home' |'order-enquire' |'order-review' |'order-sent' |'upload-prescription' |'upload-sent' |'my-prescriptions' |'refill' |'refill-sent';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fadeUp = {
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
  transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
};

function DemoBadge() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-950/50 border border-amber-900/50 text-amber-400 text-xs font-semibold tracking-wider">
      DEMO
    </span>
  );
}

function SectionBack({ label, onBack }: { label: string; onBack: () => void }) {
  return (
    <button
      onClick={onBack}
      className="flex items-center gap-2 text-xs text-white/40 hover:text-white/70 transition-colors tracking-widest uppercase mb-6 group"
    >
      <ArrowLeft size={13} className="group-hover:-translate-x-0.5 transition-transform" />
      {label}
    </button>
  );
}

// ─── Primary Action Cards ─────────────────────────────────────────────────────

interface ActionCardProps {
  icon: React.ElementType;
  title: string;
  subtitle: string;
  onClick: () => void;
  accent?: string;
}

function ActionCard({ icon: Icon, title, subtitle, onClick, accent = 'purple' }: ActionCardProps) {
  const accentMap: Record<string, string> = {
    purple: 'border-purple-500/20 hover:border-purple-500/40 group-hover:text-purple-300',
    gold: 'border-amber-500/20 hover:border-amber-500/40 group-hover:text-amber-300',
    navy: 'border-blue-500/20 hover:border-blue-500/40 group-hover:text-blue-300',
    teal: 'border-teal-500/20 hover:border-teal-500/40 group-hover:text-teal-300',
  };
  const iconAccent: Record<string, string> = {
    purple: 'text-purple-400 bg-purple-500/10',
    gold: 'text-amber-400 bg-amber-500/10',
    navy: 'text-blue-400 bg-blue-500/10',
    teal: 'text-teal-400 bg-teal-500/10',
  };

  return (
    <motion.button
      onClick={onClick}
      whileHover={{ y: -3, boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.2 }}
      className={`group w-full text-left bg-white/[0.04] border ${accentMap[accent]} rounded-2xl p-5 transition-all duration-200`}
    >
      <div className="flex items-start gap-4">
        <div className={`flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center ${iconAccent[accent]}`}>
          <Icon size={20} strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-bold tracking-widest uppercase text-white/90 transition-colors ${accentMap[accent].split(' ').find(c => c.startsWith('group-hover:'))}`}>
            {title}
          </p>
          <p className="text-xs text-white/40 mt-1 leading-relaxed">{subtitle}</p>
        </div>
        <ChevronRight size={16} className="text-white/20 group-hover:text-white/50 transition-colors flex-shrink-0 mt-0.5" />
      </div>
    </motion.button>
  );
}

// ─── ORDER / ENQUIRE FORM ─────────────────────────────────────────────────────

interface OrderEnquireFormData {
  fullName: string;
  mobile: string;
  centre: string;
  medicineRequest: string;
  hasPrescription: boolean;
}

function OrderEnquireView({
  onBack,
  onReview,
}: {
  onBack: () => void;
  onReview: (data: OrderEnquireFormData) => void;
}) {
  const { patient } = useAuth();
  const [form, setForm] = useState<OrderEnquireFormData>({
    fullName: patient?.fullName || '',
    mobile: patient?.mobile || '',
    centre: '',
    medicineRequest: '',
    hasPrescription: false,
  });
  const [errors, setErrors] = useState<Partial<OrderEnquireFormData>>({});

  const validate = () => {
    const e: Partial<Record<keyof OrderEnquireFormData, string>> = {};
    if (!form.fullName.trim()) e.fullName = 'Full name is required';
    if (!form.mobile.trim()) e.mobile = 'Mobile number is required';
    if (!form.centre) e.centre = 'Please select a centre';
    if (!form.medicineRequest.trim()) e.medicineRequest = 'Please describe your medicine request';
    setErrors(e as Partial<OrderEnquireFormData>);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) onReview(form);
  };

  const inputClass =
    'w-full bg-white/[0.05] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/25 focus:outline-none focus:border-purple-500/50 focus:bg-white/[0.07] transition-all';

  return (
    <motion.div {...fadeUp} key="order-form">
      <SectionBack label="Pharmacy" onBack={onBack} />
      <div className="mb-6">
        <p className="text-xs font-semibold tracking-widest text-purple-400/70 uppercase mb-1">PHARMACY</p>
        <h2 className="text-2xl font-bold text-white tracking-tight leading-tight">
          WHAT MEDICINES<br />DO YOU NEED?
        </h2>
        <p className="text-sm text-white/40 mt-2">
          Tell us what you need and our pharmacy team will assist you.
        </p>
      </div>

      <div className="space-y-4">
        {/* Full Name */}
        <div>
          <label className="block text-xs font-semibold tracking-widest text-white/40 uppercase mb-1.5">
            Full Name
          </label>
          <div className="relative">
            <User size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/25" />
            <input
              type="text"
              placeholder="Your full name"
              value={form.fullName}
              onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
              className={`${inputClass} pl-9`}
            />
          </div>
          {errors.fullName && <p className="text-xs text-red-400 mt-1">{errors.fullName}</p>}
        </div>

        {/* Mobile */}
        <div>
          <label className="block text-xs font-semibold tracking-widest text-white/40 uppercase mb-1.5">
            Mobile Number
          </label>
          <div className="relative">
            <Phone size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/25" />
            <input
              type="tel"
              placeholder="+91 XXXXXXXXXX"
              value={form.mobile}
              onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))}
              className={`${inputClass} pl-9`}
            />
          </div>
          {errors.mobile && <p className="text-xs text-red-400 mt-1">{errors.mobile}</p>}
        </div>

        {/* Centre */}
        <div>
          <label className="block text-xs font-semibold tracking-widest text-white/40 uppercase mb-1.5">
            Preferred All Is Well Centre
          </label>
          <div className="relative">
            <MapPin size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/25 pointer-events-none" />
            <select
              value={form.centre}
              onChange={(e) => setForm((f) => ({ ...f, centre: e.target.value }))}
              className={`${inputClass} pl-9 appearance-none cursor-pointer`}
            >
              <option value="" className="bg-[#0a1628]">Select a centre</option>
              {centres.map((c) => (
                <option key={c.id} value={c.name} className="bg-[#0a1628]">
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          {errors.centre && <p className="text-xs text-red-400 mt-1">{errors.centre}</p>}
        </div>

        {/* Medicine Request */}
        <div>
          <label className="block text-xs font-semibold tracking-widest text-white/40 uppercase mb-1.5">
            Medicine Name / Request
          </label>
          <textarea
            rows={3}
            placeholder="e.g. Metformin 500mg, or describe your requirement..."
            value={form.medicineRequest}
            onChange={(e) => setForm((f) => ({ ...f, medicineRequest: e.target.value }))}
            className={`${inputClass} resize-none`}
          />
          {errors.medicineRequest && (
            <p className="text-xs text-red-400 mt-1">{errors.medicineRequest}</p>
          )}
        </div>

        {/* Has Prescription toggle */}
        <button
          type="button"
          onClick={() => setForm((f) => ({ ...f, hasPrescription: !f.hasPrescription }))}
          className="flex items-center gap-3 text-sm text-white/50 hover:text-white/70 transition-colors"
        >
          <div
            className={`w-5 h-5 rounded border flex items-center justify-center transition-all ${
              form.hasPrescription
                ? 'bg-purple-500 border-purple-500' :'bg-transparent border-white/20'
            }`}
          >
            {form.hasPrescription && <CheckCircle2 size={12} className="text-white" />}
          </div>
          I have a prescription to share
        </button>
      </div>

      {/* Disclaimer */}
      <div className="mt-5 flex items-start gap-2.5 p-3.5 rounded-xl bg-amber-950/20 border border-amber-900/30">
        <AlertCircle size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-300/70 leading-relaxed">
          This is a pharmacy enquiry request. Availability will be confirmed by the All Is Well pharmacy team. We do not claim availability or pricing.
        </p>
      </div>

      <button
        onClick={handleSubmit}
        className="mt-5 w-full flex items-center justify-center gap-2.5 bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold text-sm tracking-widest uppercase py-3.5 rounded-xl transition-all duration-200 hover:shadow-lg hover:shadow-green-900/30"
      >
        <MessageCircle size={16} />
        REVIEW REQUEST
      </button>
    </motion.div>
  );
}

// ─── ORDER REVIEW ─────────────────────────────────────────────────────────────

function OrderReviewView({
  formData,
  requestId,
  onEdit,
  onSent,
}: {
  formData: OrderEnquireFormData;
  requestId: string;
  onEdit: () => void;
  onSent: () => void;
}) {
  const handleSend = () => {
    const msg = buildMedicineRequestWhatsAppMessage({
      requestId,
      patientName: formData.fullName,
      patientMobile: formData.mobile,
      centreName: formData.centre,
      medicineRequest: formData.medicineRequest,
      hasPrescription: formData.hasPrescription,
      createdAt: new Date().toISOString(),
    });
    openPharmacyWhatsApp(msg);
    onSent();
  };

  return (
    <motion.div {...fadeUp} key="order-review">
      <SectionBack label="Edit Details" onBack={onEdit} />
      <div className="mb-6">
        <p className="text-xs font-semibold tracking-widest text-purple-400/70 uppercase mb-1">PHARMACY REQUEST</p>
        <h2 className="text-xl font-bold text-white tracking-tight">REVIEW YOUR REQUEST</h2>
      </div>

      {/* Request ID */}
      <div className="mb-4 p-3.5 rounded-xl bg-purple-950/30 border border-purple-500/20">
        <p className="text-xs text-white/40 tracking-widest uppercase mb-0.5">Request ID</p>
        <p className="text-sm font-bold text-purple-300 font-mono">{requestId}</p>
      </div>

      {/* Details */}
      <div className="space-y-3 mb-5">
        {[
          { label: 'Patient', value: formData.fullName },
          { label: 'Mobile', value: formData.mobile },
          { label: 'Centre', value: formData.centre },
          { label: 'Medicine Request', value: formData.medicineRequest },
        ].map(({ label, value }) => (
          <div key={label} className="flex gap-3 py-2.5 border-b border-white/5">
            <span className="text-xs text-white/35 tracking-widest uppercase w-32 flex-shrink-0 pt-0.5">{label}</span>
            <span className="text-sm text-white/80 leading-relaxed">{value}</span>
          </div>
        ))}
      </div>

      {/* Status notice */}
      <div className="mb-5 flex items-start gap-2.5 p-3.5 rounded-xl bg-amber-950/20 border border-amber-900/30">
        <AlertCircle size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-bold text-amber-300 tracking-wide uppercase mb-0.5">Request — Not an Order</p>
          <p className="text-xs text-amber-300/60 leading-relaxed">
            The pharmacy team will check availability and respond to you. This is not an order confirmation.
          </p>
        </div>
      </div>

      <button
        onClick={handleSend}
        className="w-full flex items-center justify-center gap-2.5 bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold text-sm tracking-widest uppercase py-3.5 rounded-xl transition-all duration-200 hover:shadow-lg hover:shadow-green-900/30"
      >
        <MessageCircle size={16} />
        SEND REQUEST ON WHATSAPP
      </button>
      <button
        onClick={onEdit}
        className="mt-3 w-full text-center text-xs text-white/30 hover:text-white/60 transition-colors tracking-widest uppercase py-2"
      >
        EDIT DETAILS
      </button>
    </motion.div>
  );
}

// ─── REQUEST SENT ─────────────────────────────────────────────────────────────

function RequestSentView({
  requestId,
  onWhatsAppAgain,
  onHome,
  message,
  onResend,
}: {
  requestId: string;
  onWhatsAppAgain: () => void;
  onHome: () => void;
  message: string;
  onResend: () => void;
}) {
  return (
    <motion.div {...fadeUp} key="request-sent" className="text-center">
      <motion.div
        initial={{ scale: 0.7, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="w-16 h-16 rounded-full bg-green-900/30 border border-green-500/30 flex items-center justify-center mx-auto mb-5"
      >
        <CheckCircle2 size={28} className="text-green-400" />
      </motion.div>

      <p className="text-xs font-semibold tracking-widest text-green-400/70 uppercase mb-1">REQUEST RECEIVED</p>
      <h2 className="text-xl font-bold text-white tracking-tight mb-2">REQUEST PREPARED</h2>
      <p className="text-sm text-white/40 leading-relaxed max-w-xs mx-auto mb-5">
        Your request has been prepared for the All Is Well pharmacy team. Please send the WhatsApp message to complete your request.
      </p>

      <div className="inline-block px-4 py-2.5 rounded-xl bg-purple-950/30 border border-purple-500/20 mb-6">
        <p className="text-xs text-white/35 tracking-widest uppercase mb-0.5">Request ID</p>
        <p className="text-sm font-bold text-purple-300 font-mono">{requestId}</p>
      </div>

      <div className="flex items-start gap-2.5 p-3.5 rounded-xl bg-white/[0.03] border border-white/8 mb-5 text-left">
        <AlertCircle size={14} className="text-white/30 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-white/35 leading-relaxed">
          The pharmacy team will check availability and respond to you directly. This is not an order confirmation.
        </p>
      </div>

      <div className="space-y-3">
        <button
          onClick={onResend}
          className="w-full flex items-center justify-center gap-2.5 bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold text-sm tracking-widest uppercase py-3.5 rounded-xl transition-all duration-200"
        >
          <MessageCircle size={16} />
          OPEN WHATSAPP AGAIN
        </button>
        <button
          onClick={onHome}
          className="w-full text-center text-xs text-white/30 hover:text-white/60 transition-colors tracking-widest uppercase py-2"
        >
          BACK TO PHARMACY
        </button>
      </div>
    </motion.div>
  );
}

// ─── UPLOAD PRESCRIPTION ──────────────────────────────────────────────────────

function UploadPrescriptionView({ onBack }: { onBack: () => void }) {
  const { patient } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [patientName, setPatientName] = useState(patient?.fullName || '');
  const [patientMobile, setPatientMobile] = useState(patient?.mobile || '');
  const [centre, setCentre] = useState('');
  const [sent, setSent] = useState(false);
  const [requestId] = useState(() => generatePharmacyRequestId());
  const [dragOver, setDragOver] = useState(false);

  const handleFile = useCallback((file: File) => {
    const allowed = ['image/jpeg', 'image/png', 'application/pdf'];
    if (allowed.includes(file.type)) {
      setUploadedFile(file);
    }
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleSend = () => {
    const msg = `Hello All Is Well Pharmacy Team,

I would like to share a prescription for medicine assistance.

PRESCRIPTION REQUEST
━━━━━━━━━━━━━━━━

REQUEST ID:
${requestId}

PATIENT
Name: ${patientName || '[Patient Name]'}
Mobile: ${patientMobile || '[Mobile]'}

CENTRE
${centre || '[Selected Centre]'}

Note: Prescription file: ${uploadedFile?.name || 'Attached'}

Please review and assist with the medicines.

━━━━━━━━━━━━━━━━
All Is Well Experience™`;
    openPharmacyWhatsApp(msg);
    setSent(true);
  };

  if (sent) {
    return (
      <motion.div {...fadeUp} key="upload-sent" className="text-center">
        <motion.div
          initial={{ scale: 0.7, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="w-16 h-16 rounded-full bg-green-900/30 border border-green-500/30 flex items-center justify-center mx-auto mb-5"
        >
          <CheckCircle2 size={28} className="text-green-400" />
        </motion.div>
        <p className="text-xs font-semibold tracking-widest text-green-400/70 uppercase mb-1">PRESCRIPTION REQUEST READY</p>
        <h2 className="text-xl font-bold text-white tracking-tight mb-2">REQUEST PREPARED</h2>
        <p className="text-sm text-white/40 leading-relaxed max-w-xs mx-auto mb-5">
          Your request will be sent to the All Is Well pharmacy team for review.
        </p>
        <div className="inline-block px-4 py-2.5 rounded-xl bg-purple-950/30 border border-purple-500/20 mb-6">
          <p className="text-xs text-white/35 tracking-widest uppercase mb-0.5">Request ID</p>
          <p className="text-sm font-bold text-purple-300 font-mono">{requestId}</p>
        </div>
        <button
          onClick={onBack}
          className="w-full text-center text-xs text-white/30 hover:text-white/60 transition-colors tracking-widest uppercase py-2"
        >
          BACK TO PHARMACY
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div {...fadeUp} key="upload-form">
      <SectionBack label="Pharmacy" onBack={onBack} />
      <div className="mb-6">
        <p className="text-xs font-semibold tracking-widest text-purple-400/70 uppercase mb-1">PHARMACY</p>
        <h2 className="text-2xl font-bold text-white tracking-tight leading-tight">
          HAVE A<br />PRESCRIPTION?
        </h2>
        <p className="text-sm text-white/40 mt-2 leading-relaxed">
          Upload your prescription and our pharmacy team can help you with your medicine request.
        </p>
      </div>

      {/* Upload Zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`relative border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all duration-200 ${
          dragOver
            ? 'border-purple-400/60 bg-purple-500/10'
            : uploadedFile
            ? 'border-green-500/40 bg-green-900/10' :'border-white/15 hover:border-purple-500/40 hover:bg-white/[0.03]'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.pdf"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
          }}
        />
        <AnimatePresence mode="wait">
          {uploadedFile ? (
            <motion.div
              key="file-preview"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25 }}
            >
              <div className="w-12 h-12 rounded-xl bg-green-900/30 border border-green-500/30 flex items-center justify-center mx-auto mb-3">
                <FileText size={22} className="text-green-400" />
              </div>
              <p className="text-sm font-semibold text-green-300 mb-1">{uploadedFile.name}</p>
              <p className="text-xs text-white/30">
                {(uploadedFile.size / 1024).toFixed(0)} KB — Click to change
              </p>
            </motion.div>
          ) : (
            <motion.div key="upload-prompt" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="w-12 h-12 rounded-xl bg-white/[0.05] border border-white/10 flex items-center justify-center mx-auto mb-3">
                <Upload size={22} className="text-white/30" />
              </div>
              <p className="text-sm font-semibold text-white/60 mb-1">UPLOAD PRESCRIPTION</p>
              <p className="text-xs text-white/25">JPG, PNG or PDF — tap or drag to upload</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Patient details */}
      <div className="mt-5 space-y-3">
        <div className="relative">
          <User size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/25" />
          <input
            type="text"
            placeholder="Your full name"
            value={patientName}
            onChange={(e) => setPatientName(e.target.value)}
            className="w-full bg-white/[0.05] border border-white/10 rounded-xl px-4 py-3 pl-9 text-sm text-white placeholder-white/25 focus:outline-none focus:border-purple-500/50 transition-all"
          />
        </div>
        <div className="relative">
          <Phone size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/25" />
          <input
            type="tel"
            placeholder="Mobile number"
            value={patientMobile}
            onChange={(e) => setPatientMobile(e.target.value)}
            className="w-full bg-white/[0.05] border border-white/10 rounded-xl px-4 py-3 pl-9 text-sm text-white placeholder-white/25 focus:outline-none focus:border-purple-500/50 transition-all"
          />
        </div>
        <div className="relative">
          <MapPin size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/25 pointer-events-none" />
          <select
            value={centre}
            onChange={(e) => setCentre(e.target.value)}
            className="w-full bg-white/[0.05] border border-white/10 rounded-xl px-4 py-3 pl-9 text-sm text-white focus:outline-none focus:border-purple-500/50 transition-all appearance-none cursor-pointer"
          >
            <option value="" className="bg-[#0a1628]">Select a centre</option>
            {centres.map((c) => (
              <option key={c.id} value={c.name} className="bg-[#0a1628]">{c.name}</option>
            ))}
          </select>
        </div>
      </div>

      <button
        onClick={handleSend}
        disabled={!uploadedFile}
        className="mt-5 w-full flex items-center justify-center gap-2.5 bg-[#25D366] hover:bg-[#20bd5a] disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-sm tracking-widest uppercase py-3.5 rounded-xl transition-all duration-200"
      >
        <MessageCircle size={16} />
        SEND TO PHARMACY ON WHATSAPP
      </button>
    </motion.div>
  );
}

// ─── MY PRESCRIPTIONS ─────────────────────────────────────────────────────────

function MyPrescriptionsView({ onBack }: { onBack: () => void }) {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return (
      <motion.div {...fadeUp} key="rx-login">
        <SectionBack label="Pharmacy" onBack={onBack} />
        <div className="mb-6">
          <p className="text-xs font-semibold tracking-widest text-purple-400/70 uppercase mb-1">PHARMACY</p>
          <h2 className="text-2xl font-bold text-white tracking-tight">MY PRESCRIPTIONS</h2>
        </div>
        <div className="flex flex-col items-center text-center py-10">
          <div className="w-14 h-14 rounded-2xl bg-white/[0.04] border border-white/10 flex items-center justify-center mb-4">
            <Lock size={22} className="text-white/25" />
          </div>
          <p className="text-sm text-white/40 leading-relaxed max-w-xs mb-6">
            Your prescriptions will appear here after you sign in to My All Is Well.
          </p>
          <Link
            href="/my-all-is-well/login"
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-500 text-white font-bold text-sm tracking-widest uppercase px-6 py-3 rounded-xl transition-all duration-200"
          >
            <LogIn size={15} />
            LOGIN / SIGN UP
          </Link>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div {...fadeUp} key="rx-list">
      <SectionBack label="Pharmacy" onBack={onBack} />
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs font-semibold tracking-widest text-purple-400/70 uppercase mb-1">PHARMACY</p>
          <h2 className="text-2xl font-bold text-white tracking-tight">MY PRESCRIPTIONS</h2>
        </div>
        <DemoBadge />
      </div>

      <div className="mb-4 flex items-start gap-2.5 p-3 rounded-xl bg-amber-950/20 border border-amber-900/30">
        <AlertCircle size={13} className="text-amber-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-300/60 leading-relaxed">
          Demo records only. In production, prescriptions are retrieved from the secure All Is Well hospital system.
        </p>
      </div>

      <div className="space-y-3">
        {DEMO_PHARMACY_PRESCRIPTIONS.map((rx) => (
          <motion.div
            key={rx.id}
            whileHover={{ y: -2 }}
            transition={{ duration: 0.18 }}
            className="bg-white/[0.04] border border-white/8 rounded-2xl p-4"
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center justify-center flex-shrink-0">
                  <FileText size={14} className="text-purple-400" />
                </div>
                <div>
                  <p className="text-xs font-bold text-white/80 tracking-wide uppercase">{rx.label}</p>
                  <p className="text-xs text-white/35">{rx.date}</p>
                </div>
              </div>
              <DemoBadge />
            </div>
            <div className="space-y-1 mb-3">
              <p className="text-xs text-white/40">
                <span className="text-white/25 uppercase tracking-wider text-[10px]">Doctor </span>{rx.doctor}
              </p>
              <p className="text-xs text-white/40">
                <span className="text-white/25 uppercase tracking-wider text-[10px]">Centre </span>{rx.centre}
              </p>
              <p className="text-xs text-white/40">
                <span className="text-white/25 uppercase tracking-wider text-[10px]">Medicines </span>{rx.medicineCount} items
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full border ${
                rx.status === 'active' ?'bg-green-900/30 text-green-400 border-green-900/50' :'bg-white/5 text-white/30 border-white/10'
              }`}>
                {rx.status}
              </span>
              <button className="ml-auto flex items-center gap-1.5 text-xs text-purple-400 hover:text-purple-300 transition-colors tracking-widest uppercase">
                <Eye size={12} />
                VIEW PRESCRIPTION
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}

// ─── REFILL MEDICINES ─────────────────────────────────────────────────────────

function RefillView({ onBack, onSent }: { onBack: () => void; onSent: (id: string, msg: string) => void }) {
  const { isAuthenticated, patient } = useAuth();
  const [selectedRx, setSelectedRx] = useState<DemoPharmacyPrescription | null>(null);
  const [patientName, setPatientName] = useState(patient?.fullName || '');
  const [patientMobile, setPatientMobile] = useState(patient?.mobile || '');
  const [centre, setCentre] = useState('');

  if (!isAuthenticated) {
    return (
      <motion.div {...fadeUp} key="refill-login">
        <SectionBack label="Pharmacy" onBack={onBack} />
        <div className="mb-6">
          <p className="text-xs font-semibold tracking-widest text-purple-400/70 uppercase mb-1">PHARMACY</p>
          <h2 className="text-2xl font-bold text-white tracking-tight">NEED A REFILL?</h2>
        </div>
        <div className="flex flex-col items-center text-center py-10">
          <div className="w-14 h-14 rounded-2xl bg-white/[0.04] border border-white/10 flex items-center justify-center mb-4">
            <Lock size={22} className="text-white/25" />
          </div>
          <p className="text-sm text-white/40 leading-relaxed max-w-xs mb-6">
            Sign in to see your previous prescriptions and request a refill.
          </p>
          <Link
            href="/my-all-is-well/login"
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-500 text-white font-bold text-sm tracking-widest uppercase px-6 py-3 rounded-xl transition-all duration-200"
          >
            <LogIn size={15} />
            LOGIN / SIGN UP
          </Link>
        </div>
      </motion.div>
    );
  }

  const handleRefill = () => {
    if (!selectedRx) return;
    const requestId = generatePharmacyRequestId();
    const msg = buildRefillRequestWhatsAppMessage({
      requestId,
      patientName: patientName || patient?.fullName || '',
      patientMobile: patientMobile || patient?.mobile || '',
      centreName: centre || 'Not specified',
      prescriptionId: selectedRx.id,
      prescriptionLabel: selectedRx.label,
      doctor: selectedRx.doctor,
      createdAt: new Date().toISOString(),
    });
    openPharmacyWhatsApp(msg);
    onSent(requestId, msg);
  };

  return (
    <motion.div {...fadeUp} key="refill-form">
      <SectionBack label="Pharmacy" onBack={onBack} />
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs font-semibold tracking-widest text-purple-400/70 uppercase mb-1">PHARMACY</p>
          <h2 className="text-2xl font-bold text-white tracking-tight">NEED A REFILL?</h2>
        </div>
        <DemoBadge />
      </div>

      <p className="text-sm text-white/40 mb-4">Select a previous prescription to request a refill.</p>

      <div className="space-y-2 mb-5">
        {DEMO_PHARMACY_PRESCRIPTIONS.filter((rx) => rx.status === 'active').map((rx) => (
          <button
            key={rx.id}
            onClick={() => setSelectedRx(rx)}
            className={`w-full text-left p-4 rounded-xl border transition-all duration-200 ${
              selectedRx?.id === rx.id
                ? 'border-purple-500/50 bg-purple-950/30' :'border-white/8 bg-white/[0.03] hover:border-white/15'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                selectedRx?.id === rx.id ? 'border-purple-400 bg-purple-400' : 'border-white/20'
              }`}>
                {selectedRx?.id === rx.id && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
              </div>
              <div>
                <p className="text-xs font-bold text-white/80 tracking-wide uppercase">{rx.label}</p>
                <p className="text-xs text-white/35">{rx.doctor} · {rx.date} · {rx.medicineCount} medicines</p>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Contact details */}
      <div className="space-y-3 mb-5">
        <div className="relative">
          <User size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/25" />
          <input
            type="text"
            placeholder="Your full name"
            value={patientName}
            onChange={(e) => setPatientName(e.target.value)}
            className="w-full bg-white/[0.05] border border-white/10 rounded-xl px-4 py-3 pl-9 text-sm text-white placeholder-white/25 focus:outline-none focus:border-purple-500/50 transition-all"
          />
        </div>
        <div className="relative">
          <Phone size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/25" />
          <input
            type="tel"
            placeholder="Mobile number"
            value={patientMobile}
            onChange={(e) => setPatientMobile(e.target.value)}
            className="w-full bg-white/[0.05] border border-white/10 rounded-xl px-4 py-3 pl-9 text-sm text-white placeholder-white/25 focus:outline-none focus:border-purple-500/50 transition-all"
          />
        </div>
        <div className="relative">
          <MapPin size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/25 pointer-events-none" />
          <select
            value={centre}
            onChange={(e) => setCentre(e.target.value)}
            className="w-full bg-white/[0.05] border border-white/10 rounded-xl px-4 py-3 pl-9 text-sm text-white focus:outline-none focus:border-purple-500/50 transition-all appearance-none cursor-pointer"
          >
            <option value="" className="bg-[#0a1628]">Select a centre</option>
            {centres.map((c) => (
              <option key={c.id} value={c.name} className="bg-[#0a1628]">{c.name}</option>
            ))}
          </select>
        </div>
      </div>

      <button
        onClick={handleRefill}
        disabled={!selectedRx}
        className="w-full flex items-center justify-center gap-2.5 bg-[#25D366] hover:bg-[#20bd5a] disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-sm tracking-widest uppercase py-3.5 rounded-xl transition-all duration-200"
      >
        <MessageCircle size={16} />
        REQUEST REFILL ON WHATSAPP
      </button>
    </motion.div>
  );
}

// ─── PHARMACY HOME ────────────────────────────────────────────────────────────

export default function PharmacyContent() {
  const [view, setView] = useState<PharmacyView>('home');
  const [orderFormData, setOrderFormData] = useState<OrderEnquireFormData | null>(null);
  const [requestId, setRequestId] = useState('');
  const [whatsAppMessage, setWhatsAppMessage] = useState('');

  const handleOrderReview = (data: OrderEnquireFormData) => {
    setOrderFormData(data);
    setRequestId(generatePharmacyRequestId());
    setView('order-review');
  };

  const handleOrderSent = () => {
    setView('order-sent');
  };

  const handleRefillSent = (id: string, msg: string) => {
    setRequestId(id);
    setWhatsAppMessage(msg);
    setView('refill-sent');
  };

  const handleResend = () => {
    if (whatsAppMessage) openPharmacyWhatsApp(whatsAppMessage);
    else if (orderFormData) {
      const msg = buildMedicineRequestWhatsAppMessage({
        requestId,
        patientName: orderFormData.fullName,
        patientMobile: orderFormData.mobile,
        centreName: orderFormData.centre,
        medicineRequest: orderFormData.medicineRequest,
        hasPrescription: orderFormData.hasPrescription,
        createdAt: new Date().toISOString(),
      });
      openPharmacyWhatsApp(msg);
    }
  };

  return (
    <div className="min-h-screen bg-[#060d1a]">
      <TopNav />

      {/* Hero — only shown on home */}
      <AnimatePresence mode="wait">
        {view === 'home' && (
          <motion.div
            key="pharmacy-hero"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="relative overflow-hidden"
          >
            {/* Background gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#0d1f3c] via-[#060d1a] to-[#0a0f1e]" />
            <div className="absolute top-0 right-0 w-96 h-96 bg-purple-600/5 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-amber-500/3 rounded-full blur-3xl" />

            <div className="relative max-w-3xl mx-auto px-5 pt-16 pb-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <p className="text-xs font-semibold tracking-widest text-purple-400/60 uppercase mb-3">
                  ALL IS WELL PHARMACY
                </p>
                <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight leading-[1.1] mb-4">
                  YOUR MEDICINES,<br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-300 to-purple-500">
                    MADE SIMPLE.
                  </span>
                </h1>
                <p className="text-base text-white/45 leading-relaxed max-w-md">
                  Get help with prescriptions, medicine availability and refills from All Is Well.
                </p>
              </motion.div>

              {/* Subtle gold divider */}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="mt-8 h-px bg-gradient-to-r from-amber-500/30 via-amber-400/10 to-transparent origin-left"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main content */}
      <div className="max-w-3xl mx-auto px-5 pb-20">
        <AnimatePresence mode="wait">
          {view === 'home' && (
            <motion.div
              key="home"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            >
              {/* Future architecture notice */}
              <div className="mb-6 flex items-start gap-2.5 p-3.5 rounded-xl bg-white/[0.03] border border-white/8">
                <AlertCircle size={13} className="text-white/25 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-white/25 leading-relaxed">
                  Phase 1 pharmacy request system. Requests are sent to the All Is Well pharmacy team via WhatsApp. Availability is confirmed by the team directly.
                </p>
              </div>

              {/* 4 Primary Actions */}
              <div className="space-y-3">
                <ActionCard
                  icon={ClipboardList}
                  title="ORDER / ENQUIRE ABOUT MEDICINES"
                  subtitle="Tell us what medicines you need and our team will assist you."
                  onClick={() => setView('order-enquire')}
                  accent="purple"
                />
                <ActionCard
                  icon={Upload}
                  title="UPLOAD PRESCRIPTION"
                  subtitle="Share your prescription and our pharmacy team will help with your request."
                  onClick={() => setView('upload-prescription')}
                  accent="gold"
                />
                <ActionCard
                  icon={FileText}
                  title="MY PRESCRIPTIONS"
                  subtitle="View your prescription history and manage your medicines."
                  onClick={() => setView('my-prescriptions')}
                  accent="navy"
                />
                <ActionCard
                  icon={RefreshCw}
                  title="REFILL MEDICINES"
                  subtitle="Request a refill for a previous prescription from All Is Well."
                  onClick={() => setView('refill')}
                  accent="teal"
                />
              </div>

              {/* WhatsApp direct */}
              <div className="mt-8 p-4 rounded-2xl bg-white/[0.03] border border-white/8 flex items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-bold text-white/60 tracking-widest uppercase mb-0.5">Need immediate help?</p>
                  <p className="text-xs text-white/30">Contact the All Is Well pharmacy team directly.</p>
                </div>
                <a
                  href={`${hospitalConfig.officialWhatsAppLink}?text=${encodeURIComponent('Hello All Is Well Pharmacy Team, I need assistance with medicines.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 flex items-center gap-2 bg-[#25D366]/10 hover:bg-[#25D366]/20 border border-[#25D366]/30 text-[#25D366] font-bold text-xs tracking-widest uppercase px-4 py-2.5 rounded-xl transition-all duration-200"
                >
                  <MessageCircle size={14} />
                  WHATSAPP
                </a>
              </div>
            </motion.div>
          )}

          {view === 'order-enquire' && (
            <OrderEnquireView
              key="order-enquire"
              onBack={() => setView('home')}
              onReview={handleOrderReview}
            />
          )}

          {view === 'order-review' && orderFormData && (
            <OrderReviewView
              key="order-review"
              formData={orderFormData}
              requestId={requestId}
              onEdit={() => setView('order-enquire')}
              onSent={handleOrderSent}
            />
          )}

          {(view === 'order-sent' || view === 'refill-sent') && (
            <RequestSentView
              key="request-sent"
              requestId={requestId}
              onWhatsAppAgain={handleResend}
              onHome={() => setView('home')}
              message={whatsAppMessage}
              onResend={handleResend}
            />
          )}

          {view === 'upload-prescription' && (
            <UploadPrescriptionView
              key="upload-prescription"
              onBack={() => setView('home')}
            />
          )}

          {view === 'my-prescriptions' && (
            <MyPrescriptionsView
              key="my-prescriptions"
              onBack={() => setView('home')}
            />
          )}

          {view === 'refill' && (
            <RefillView
              key="refill"
              onBack={() => setView('home')}
              onSent={handleRefillSent}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
